import type { trackEcommerce } from '@svenskaspelab/nova-core/nextjs/analytics';

import type { CancelWager } from '../../cancel/Cancel.js';
import { createCancellation, type PostMiddlewareFn, type PreMiddlewareFn } from '../../cancel/createCancellation.js';
import type { CancelWagerError, MiddlewarePaymentError } from '../../error/index.js';

type CancelWagersParams = {
  wagers: CancelWager[];
  preMiddlewares?: PreMiddlewareFn[];
  postMiddlewares?: PostMiddlewareFn[];
  trackTransaction?: typeof trackEcommerce;
};

/**
 * Function to cancel wagers and set up flow.
 *
 * @param root0 params
 * @param root0.wagers wagers to cancel
 * @param root0.preMiddlewares pre middlewares to run before cancel request
 * @param root0.postMiddlewares post middlewares to run after cancel request
 * @param root0.trackTransaction optional ecommerce tracking callback
 * @returns result and error from cancelation process
 */
export async function cancelWagers({
  wagers,
  preMiddlewares,
  postMiddlewares,
  trackTransaction,
}: CancelWagersParams): Promise<
  readonly [WeakMap<CancelWager, unknown>, CancelWagerError | MiddlewarePaymentError | Error | undefined]
> {
  const cancelBuilder = createCancellation(wagers, { trackTransaction });

  if (preMiddlewares && preMiddlewares.length > 0) {
    for (const middleware of preMiddlewares) {
      cancelBuilder.addPreMiddleware(middleware);
    }
  }

  if (postMiddlewares && postMiddlewares.length > 0) {
    for (const middleware of postMiddlewares) {
      cancelBuilder.addPostMiddleware(middleware);
    }
  }

  const [result, error] = await cancelBuilder.execute();

  return [result, error] as const;
}
